import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import OpenAI from "openai";
import cardBg from "../images/memetag/meme_sub_tag_cn.png";
import bgMain from "../images/bg-main.png";
import PromptEditor from "./PromptEditor";
import ShareButtons from "./common/ShareButtons";
import {
  getWalletData,
  fetchWalletSummary,
  formatTradeSummary,
} from "../services/walletService";

import pSmallImage from "../images/memetag/types/p_small.png";
import pBigImage from "../images/memetag/types/p_big.png";
import pMarshalImage from "../images/memetag/types/p_marshal.png";
import chainKingImage from "../images/memetag/types/chain_king.png";
import bscPrinceImage from "../images/memetag/types/bsc_prince.png";
import blockKingImage from "../images/memetag/types/block_king.png";
import diamondImage from "../images/memetag/types/diamond_hands.png";
import trainHeadImage from "../images/memetag/types/train_head.png";

// 暂时移除特定标签图片导入，使用cardBg作为占位图

const MemeCoinTagCNDetail = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("form");
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState("");
  const [parsedResult, setParsedResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressStatus, setProgressStatus] = useState("分析中...");
  const [streamComplete, setStreamComplete] = useState(false);
  const [controller, setController] = useState(null);
  const resultRef = useRef(null);
  const [currentPrompt, setCurrentPrompt] = useState(`分析以下交易：

{{交易摘要}}

请根据以下规则进行分类，并返回相应的标签：

1. 如果用户资产小于10K，标签为"P小将"
2. 如果用户资产在10K-100K之间，标签为"P大将"
3. 如果用户资产大于100K，标签为"P元帅"
4. 如果用户持有过CZ或CX认证过的币（如$TUT），标签为"BSC链太子"
5. 如果用户盈利率超过50%，标签为"链上皇"
6. 如果用户亏损率超过20%，标签为"盖帽王"
7. 如果用户长期持有未交易（买了就忘了），标签为"钻石手"
8. 如果用户某个代币持仓超过1%，标签为"火车头"

请以JSON格式返回以下信息：
{
  "tag": "", // 交易风格（如果多种模式匹配，从上面选择最匹配的一种）
  "behavior": "", // 简短描述用户的交易行为
  "humor": "" // 幽默简短的行为描述（100-200字）： 在里面把哪些币被交易了，亏损百分比，交易频率这些具体数据和币的ticker提出来，不要太空泛。要基于最显著行为的幽默，解释什么是上面的风格（比如P小将，P大将，火车头），然后是把这些ticker和涨跌幅度都配上，目标是让用户觉得很有web3 vibe的搞笑，愿意做x上分享。重要：必须使用第二人称（"你"）来描述用户，而不是第三人称，就像是直接对用户说话。
}

确保返回的是有效的JSON格式，不要有任何其他文本。`);
  // 这些状态变量用于钱包摘要功能
  const [summaryData, setSummaryData] = useState(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [tradeSummary, setTradeSummary] = useState("");
  const [promptReady, setPromptReady] = useState(false);
  const [walletAddress, setWalletAddress] = useState("");

  // 处理返回按钮
  const handleBackToForm = () => {
    if (parsedResult && step === "result") {
      // 如果在结果页面，先返回到表单状态
      setParsedResult(null);
      setStep("form");
      setResult("");
      setProgress(0);
      setStreamComplete(false);
      if (controller) {
        controller.abort();
      }
    } else {
      // 否则返回到首页
      navigate(-1);
    }
  };

  // 获取钱包数据
  const handleGetWalletData = useCallback(
    async (address) => {
      if (!address) return;

      try {
        setIsSummaryLoading(true);

        // 获取钱包数据
        const walletData = await getWalletData(address);

        if (!walletData) {
          console.log("No transaction records found, trying to refresh data");
          // 如果没有数据或交易为空，尝试刷新数据
          await fetchWalletSummary(address);
          const refreshedData = await getWalletData(address);
          setSummaryData(refreshedData);

          // 格式化交易摘要
          if (refreshedData) {
            const summary = formatTradeSummary(refreshedData);
            setTradeSummary(summary);
            // 替换提示词中的占位符
            const updatedPrompt = currentPrompt.replace(
              "{{交易摘要}}",
              summary
            );
            setCurrentPrompt(updatedPrompt);
            setPromptReady(true);
          } else {
            console.error("No trade data available after refresh");
          }
        } else {
          // 如果有数据，直接使用
          setSummaryData(walletData);

          // 格式化交易摘要
          const summary = formatTradeSummary(walletData);
          setTradeSummary(summary);
          // 替换提示词中的占位符
          const updatedPrompt = currentPrompt.replace("{{交易摘要}}", summary);
          setCurrentPrompt(updatedPrompt);
          setPromptReady(true);
        }
      } catch (error) {
        console.error("Error getting wallet data:", error);
      } finally {
        setIsSummaryLoading(false);
      }
    },
    [currentPrompt]
  );

  // 获取钱包地址和数据
  useEffect(() => {
    const savedAddress = localStorage.getItem("bscAddress");
    if (savedAddress) {
      setWalletAddress(savedAddress);
      handleGetWalletData(savedAddress);
    }
  }, [handleGetWalletData]);

  // 处理表单提交和API请求
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setStep("result");

    // 创建一个可中止的控制器
    const abortController = new AbortController();
    setController(abortController);

    // 创建OpenAI客户端
    const openai = new OpenAI({
      baseURL: "https://api.deepseek.com",
      apiKey: "sk-326e542c536742f3bb5f4d8b019a614e",
      dangerouslyAllowBrowser: true,
    });

    // 初始化进度
    setProgress(0);
    setProgressStatus("分析中...");

    // 模拟进度更新
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) {
          clearInterval(progressInterval);
          return prev;
        }
        const increment = Math.random() * 10;
        const newProgress = Math.min(prev + increment, 95);

        // 根据进度更新状态消息
        if (newProgress < 30) {
          setProgressStatus("分析钱包交易...");
        } else if (newProgress < 60) {
          setProgressStatus("识别交易模式...");
        } else if (newProgress < 90) {
          setProgressStatus("生成memecoin交易标签...");
        }

        return newProgress;
      });
    }, 1000);

    try {
      // Get BSC address from localStorage if not in form
      const bscAddress = walletAddress || localStorage.getItem("bscAddress");

      if (!bscAddress) {
        throw new Error("BSC address is required");
      }

      // Build prompt - 使用已经准备好的prompt
      const prompt = currentPrompt;

      // 发送请求到OpenAI（非流式）
      const response = await openai.chat.completions.create(
        {
          model: "deepseek-chat",
          messages: [{ role: "system", content: prompt }],
          stream: false,
        },
        { signal: abortController.signal }
      );

      // 获取完整的响应文本
      const responseText = response.choices[0]?.message?.content || "";
      setResult(responseText);

      // 尝试解析JSON
      try {
        // 处理可能的Markdown代码块
        let cleanedResponse = responseText;
        if (responseText.includes("```json")) {
          cleanedResponse = responseText.replace(/```json\n|```/g, "");
        }

        // 查找JSON对象的开始和结束
        const jsonStart = cleanedResponse.indexOf("{");
        const jsonEnd = cleanedResponse.lastIndexOf("}");

        if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
          const jsonStr = cleanedResponse.substring(jsonStart, jsonEnd + 1);
          const parsed = JSON.parse(jsonStr);
          if (parsed && parsed.tag) {
            // 如果没有title字段，根据tag生成一个标题
            if (!parsed.title) {
              // 根据tag生成对应的标题
              const tagTitleMap = {
                P小将: "P小将",
                P大将: "P大将",
                P元帅: "P元帅",
                BSC链太子: "BSC链太子",
                链上皇: "链上皇",
                盖帽王: "盖帽王",
                钻石手: "钻石手",
                火车头: "火车头",
              };
              parsed.title = tagTitleMap[parsed.tag] || `${parsed.tag}`;
            }

            // 根据标签设置固定文案
            const tagBehaviorMap = {
              P小将:
                "买的早,但是又拿不住,害怕起飞又害怕一根大阴线,反复买入卖出.....P小将真实心理写照",
              P大将: "链上豪掷千万，线下却舍不得20块外卖",
              P元帅: "苦练扫链技术, 从此不会枯坐啦",
              BSC链太子: "太子驾到，诸邪退避",
              链上皇: "区块链大明星, 群友实力超乎想象，越浇韭菜越跟啊",
              盖帽王: "建议多学习一下打狗技巧，怎么每次刚买入就浇给了",
              钻石手: "又钻地了",
              火车头:
                "他可能不是链上影响力最大的kol 但一定是敌人最多的kol\n你敢发车兄弟们就敢上车",
            };

            if (tagBehaviorMap[parsed.tag]) {
              parsed.behavior = tagBehaviorMap[parsed.tag];
            }

            setParsedResult(parsed);
          }
        }
      } catch (jsonError) {
        console.error("JSON parsing failed:", jsonError);
      }

      // 完成后更新进度到100%
      setProgress(100);
      setProgressStatus("分析完成！");
      setStreamComplete(true);
    } catch (error) {
      console.error("Error:", error);
      setResult(`Error: ${error.message}`);
      setStreamComplete(true);
    } finally {
      // 清除进度条定时器
      clearInterval(progressInterval);
      setIsLoading(false);
    }
  };

  // 确保每次结果更新时滚动到底部
  useEffect(() => {
    if (resultRef.current && result) {
      if (resultRef.current) {
        resultRef.current.scrollTop = resultRef.current.scrollHeight;
      }
    }
  }, [result, streamComplete]);

  // 组件卸载时清理资源
  useEffect(() => {
    return () => {
      if (controller) {
        controller.abort();
      }
    };
  }, [controller]);

  // Handle prompt update from PromptEditor
  const handleSendPrompt = (newPrompt) => {
    setCurrentPrompt(newPrompt);
  };

  // Get the appropriate image based on tag
  const getTagImage = (tag) => {
    if (!tag) return cardBg;

    const tagMap = {
      P小将: pSmallImage,
      P大将: pBigImage,
      P元帅: pMarshalImage,
      BSC链太子: bscPrinceImage,
      链上皇: chainKingImage,
      盖帽王: blockKingImage,
      钻石手: diamondImage,
      火车头: trainHeadImage,
    };

    return tagMap[tag] || cardBg;
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      {/* Background elements */}
      <div className="fixed inset-0 overflow-hidden">
        <div
          className="absolute w-full h-full overflow-hidden"
          style={{
            backgroundImage: `url(${bgMain})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.8,
          }}
        ></div>
      </div>

      {/* Main content */}
      <div className="relative z-10 w-full max-w-md mx-auto pt-8 px-4 pb-20">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 relative">
          <button
            onClick={handleBackToForm}
            className="text-[#1c1f25] hover:text-[#f784ff] transition-colors z-10"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h2 className="text-[#1c1f25] text-xl font-medium absolute left-0 right-0 text-center">
            Memecoin Tag
          </h2>
        </div>

        <AnimatePresence mode="wait">
          {/* Main card with progress bar - Show when loading but no result yet */}
          {(step === "form" || (isLoading && !parsedResult)) && (
            <motion.div
              key="main-card"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
              className="mt-6 w-[345px] mx-auto"
            >
              {/* Memecoin Tag Card - Based on Figma design */}
              <div className="flex flex-col w-[345px] items-start gap-[15px] p-[15px] relative bg-white rounded-[16px] overflow-hidden shadow-lg mx-auto">
                <div className="flex flex-col items-center gap-2.5 relative self-stretch w-full flex-[0_0_auto]">
                  {/* Card Header with background image */}
                  <img
                    className="relative self-stretch w-full h-[255px] object-cover"
                    src={cardBg}
                    alt="Card Background"
                  />

                  {/* Title */}
                  <p className="relative w-fit font-medium text-[#1c1f25] text-xl tracking-[0] leading-[35px] whitespace-nowrap">
                    你是Meme币杀手还是接盘侠？
                  </p>

                  {/* Subtitle */}
                  <p className="relative self-stretch font-normal text-[#676C77] text-base tracking-[0] leading-[23px]">
                    测测你的Meme币交易DNA！链上行为一键解析，送你专属交易称号+毒舌辣评，笑着做链上明白人。
                  </p>

                  {/* Progress bar inside card */}
                  {isLoading && (
                    <div className="w-full mt-4 flex flex-col items-center">
                      <div className="flex flex-col w-full items-start gap-2.5">
                        {/* Progress bar */}
                        <div className="flex flex-col w-full items-start gap-2.5 relative flex-[0_0_auto] bg-[#cacfdb] rounded-[100px] border-[0.5px] border-solid border-[#E5E7EB]">
                          <div
                            className="relative h-[15px] rounded-[100px]"
                            style={{
                              width: `${progress}%`,
                              background:
                                "linear-gradient(72deg, rgba(112,235,122,1) 1%, rgba(95,217,200,1) 1%, rgba(112,168,235,1) 36%, rgba(116,166,236,1) 45%, rgba(179,142,255,1) 64%, rgba(227,86,197,1) 85%, rgba(249,173,88,1) 100%)",
                            }}
                          />
                        </div>

                        {/* Status text */}
                        <div className="relative self-stretch font-normal text-[#959AA3] text-sm text-center tracking-[0] leading-[23px]">
                          {Math.floor(progress)}%&nbsp;&nbsp;&nbsp;&nbsp;
                          {progressStatus}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Button below card - Only show in form step */}
              {step === "form" && (
                <motion.form
                  onSubmit={handleSubmit}
                  key="form-button"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="mt-6 w-[345px] mx-auto"
                >
                  <motion.button
                    type="submit"
                    className="w-full bg-gradient-to-r from-[#f784ff] to-[#b784ff] text-white py-3 rounded-lg font-medium hover:opacity-90 transition-all shadow-md flex items-center justify-center"
                    whileHover={{
                      scale: 1.03,
                      boxShadow: "0 10px 25px -5px rgba(247, 132, 255, 0.4)",
                    }}
                    whileTap={{ scale: 0.97 }}
                    disabled={isLoading || !promptReady || isSummaryLoading}
                  >
                    {isLoading ? (
                      <div className="flex items-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Analyzing...
                      </div>
                    ) : isSummaryLoading ? (
                      <div className="flex items-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Loading wallet data...
                      </div>
                    ) : (
                      "开始分析"
                    )}
                  </motion.button>
                </motion.form>
              )}
            </motion.div>
          )}

          {/* Result card - Only show when there's a parsed result */}
          {parsedResult && (
            <motion.div
              key="result-content"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
              className="mt-6 w-[355px] mx-auto"
            >
              {/* New Result Card Design */}
              <div
                id="meme-tag-card"
                className="relative w-[355px] min-h-[650px] bg-[#0a0e1f] rounded-[16px] overflow-hidden border border-solid border-transparent pb-6"
              >
                {/* Tag Image */}
                <motion.img
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="w-[355px] h-[345px] object-cover"
                  src={getTagImage(parsedResult.tag)}
                  alt={parsedResult.tag}
                />

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="px-7 flex flex-col space-y-5"
                >
                  {/* Title */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.3 }}
                    className="w-full mt-5 font-bold text-[22px] text-center tracking-[0] leading-[normal] text-[#f784ff]"
                  >
                    {parsedResult.title || parsedResult.tag}
                  </motion.div>

                  {/* Behavior Description */}
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.4 }}
                    className="w-full font-normal text-[#f5f5f5] text-base tracking-[0] leading-[normal]"
                  >
                    <span className="font-medium whitespace-pre-line">
                      {parsedResult.behavior}
                    </span>
                  </motion.div>

                  {/* Humor Comment */}
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.5 }}
                    className="w-full text-[#a0a0a0] text-sm italic tracking-[0] leading-[normal] border-l-2 border-[#3f51b5] pl-3 mt-2"
                  >
                    <span className="whitespace-pre-line">
                      {parsedResult.humor}
                    </span>
                  </motion.div>
                </motion.div>
              </div>

              {/* 底部按钮组 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.6 }}
              >
                <ShareButtons
                  cardId="meme-tag-card"
                  data={parsedResult}
                  featureName="你是Meme币杀手还是接盘侠？"
                  shareText={`See my Memecoin tag and then try yours: https://dapp.pinai.tech\n${parsedResult?.tag || ''} - ${parsedResult?.title || ''}${parsedResult?.humor ? `\n${parsedResult.humor}` : ''}`}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Prompt Editor - Only show when data is loaded, not analyzing, and in form step */}
        {promptReady && !isLoading && step === "form" && (
          <PromptEditor
            updatePrompt={currentPrompt}
            onSendPrompt={handleSendPrompt}
          />
        )}
      </div>
    </div>
  );
};

export default MemeCoinTagCNDetail;
